<template>
  <div
    @change="$emit('toggle')"
    @drop.prevent="onDrop"
    @dragstart.prevent
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @dragover.prevent="onDragOver"
  >
    <PositionWarning
      :show="showPositionWarning"
      :message="positionWarning"
      @dismiss="dismissWarning"
    />
    <CropImage
      :is-show="isCropMode"
      :src="target.getSrc()"
      @onSave="(data) => onChange('newCrop', data)"
      @onCancel="isCropMode = false"
      @onAutoCrop="handleAutoCrop"
    />

    <template v-if="options.isSelectImageFromBackground">
      <b-form-group>
        <b-button
          variant="primary"
          @click="onChange('selectBackground')"
        >
          <b-icon icon="cloud-upload" /> {{ `${imageSrc && !imageIsDefault ? 'Replace' : 'Select…'}` }}
        </b-button>
        <b-button
          v-b-tooltip.hover
          title="Choose product image..."
          variant="primary"
          @click="toggleProductDropdown"
        >
          <img
            src="/img/icons/choose-product.png"
            alt="choose-product-icon"
            width="20"
            height="20"
          >
        </b-button>
        <span 
          v-if="imageSrc"
          :href="imageSrc"
          class="download-image"
          download
          @click.stop
        >
          <button 
            class="close-btn"
            @click.stop="onChange('deleteConfirmationImage')" 
          >
            ✖
          </button>
          <b-img
            v-if="imageSrc"
            :src="imageSrc"
            class="img-preview"
            fluid
            rounded
          />
          <a
            v-b-tooltip.hover.viewport.noninteractive="'Download'"
            :href="imageSrc"
            class="download-image-href"
            download
            @click.stop
          >
            <b-icon icon="download" />
          </a>
          <button
            v-b-tooltip.hover.viewport.noninteractive="'Edit image'"
            class="edit-image-btn"
            @click.stop="showImageEditorModal"
          >
            <b-icon icon="pencil-square" />
          </button>
        </span>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="isClippingMask"
          @change="onChange('setIsClippingMask', isClippingMask)"
        >
          Create Clipping Mask
        </b-form-checkbox>
      </b-form-group>
      <b-form-group
        label="Theme"
        label-cols="3"
        invalid-feedback="No theme selected"
        :state="!!selectedTheme"
      >
        <Multiselect
          v-model="selectedThemeObj"
          :options="cThemesOptions"
          label="name"
          track-by="id"
          @input="onChange('selectTheme', selectedTheme)"
        />
      </b-form-group>
    </template>

    <b-form-group
      v-if="options.isBackgroundThemeImage"
      label="Border color"
      label-cols="4"
    >
      <div class="d-flex">
        <ColorPicker
          :value="target.stroke"
          :themes="themes"
          copy
          @input="onChange('strokeColor', $event)"
        />
        <b-button
          v-b-tooltip.hover
          variant="primary"
          size="sm"
          title="Use background color"
          @click="onChange('strokeColor', target.fill)"
        >
          <b-icon icon="back" />
        </b-button>
      </div>
    </b-form-group>

    <b-form-group
      v-if="options.isBackgroundThemeImage"
      label="Border width"
      :disabled="target.locked"
    >
      <b-input-group>
        <b-input
          v-model="localStrokeWidth"
          :min="0"
          :max="maxStrokeWidth"
          type="range"
          @input="onChange('strokeWidth', localStrokeWidth)"
        />
        <template #append>
          <b-input
            v-model="localStrokeWidth"
            :min="0"
            :max="maxStrokeWidth"
            type="number"
            @input="onChange('strokeWidth', localStrokeWidth)"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <b-form-group
      v-if="options.isUploadImage"
      label="Image"
      label-cols="3"
    >
      <b-form-file
        placeholder="Choose a file"
        drop-placeholder="Drop file here"
        @input="onChange('upload', $event)"
      />
    </b-form-group>

    <template v-if="options.isOutline">
      <b-form-group
        label="Outline"
        label-cols="3"
      >
        <b-form-input
          v-model="objectInput.fileOutlineId"
          @change="onChange('productOutline', outlineData)"
        />
      </b-form-group>
      <b-form-group
        label="Color"
        label-cols="3"
      >
        <ColorPicker
          v-model="objectInput.fileOutlineColor"
          @input="onChange('productOutline', outlineData)"
        />
      </b-form-group>
    </template>

    <ProductImageDropdown
      ref="productImageDropdown"
      v-model="objectInput.fileIds"
      :is-file-image="options.isFileImage"
      :is-tab-active="isTabActive"
      @input="onChange('productImage', objectInput.fileIds)"
      @uploadInput="(fileIds) => onChange('productImage',{productIds: fileIds, type: 'uploaded_product_image'})"
    />
    <ObjectFillFit 
      v-if="target && target.id.startsWith('bk_theme_img_')"
      :target="target" 
    />
    <b-button
      v-show="options.isHtmlTable"
      class="mb-2"
      variant="success"
      @click="initTableEditor"
    >
      Edit Table
    </b-button>
    <b-form-group label="Opacity">
      <b-input-group>
        <b-input
          v-model="opacity"
          min="0"
          max="1"
          step="0.01"
          type="range"
          @input="onChange('opacity', $event)"
        />
        <template #append>
          <b-input
            v-model="opacity"
            min="0"
            max="1"
            step="0.01"
            type="number"
            @input="onChange('opacity', $event)"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <b-form-group
      :disabled="target.locked"
    >
      <template #label>
        <div class="d-flex justify-content-between">
          <span>Scale</span>
          <span>{{ percentOfTemplate }}% of Template</span>
        </div>
      </template>
      <b-input-group>
        <b-input
          v-model="scale"
          min="0"
          max="10"
          step="0.001"
          type="range"
          :disabled="isScaleDisabled"
          @input="onChange('scale', $event)"
        />
        <template #append>
          <b-input
            v-model="scale"
            min="0"
            max="10"
            step="0.001"
            type="number"
            :disabled="isScaleDisabled"
            @input="onChange('scale', $event)"
          />
        </template>
      </b-input-group>
    </b-form-group>

    <PositionAndSize
      :x="parseFloat(objectInput.left)"
      :y="parseFloat(objectInput.top)"
      :h="parseFloat(objectInput.height)"
      :w="parseFloat(objectInput.width)"
      aspect-ratio-option:
      :is-scale-disabled="isScaleDisabled"
      @input="onChange"
    />

    <b-form-row v-if="target.fieldType === `HTML Table`">
      <b-col>
        <b-form-group
          label="Related field"
        >
          <b-form-select
            :value="target.relatedField?.field"
            :options="relatedTemplateFields"
            @change="onChange('changeRelatedField', $event)"
          />
        </b-form-group>
      </b-col>

      <b-col>
        <b-form-group label="Margin">
          <b-form-input
            :value="target.relatedField?.margin"
            type="number"
            min="0"
            step="1"
            @input="onChange('changeMargin', $event)"
          />
        </b-form-group>
      </b-col>
    </b-form-row>

    <div class="d-flex justify-content-between align-items-start mb-3">
      <ApplyToAllImageDropdown
        :target="target"
      />
      <div>
        <b-form-checkbox
          v-if="options.isFileImage"
          v-model="objectInput.isAutoPosition"
          class="mt-1 d-none"
          size="sm"
          @change="onChange('autoPosition', objectInput.isAutoPosition)"
        >
          Auto position
        </b-form-checkbox>
      </div>
      <div class="image-editor-actions image-editor-actions--bg-actions mt-5">
        <button
          v-if="options.isSelectImageFromBackground"
          v-b-tooltip.hover
          :title="resolveScaleImageAction('width').tooltip"
          @click="onChange(resolveScaleImageAction('width').action, 'width')"
        >
          <img src="/img/icons/expand.svg">
        </button>
        <button
          v-if="options.isSelectImageFromBackground"
          v-b-tooltip.hover
          :title="resolveScaleImageAction('height').tooltip"
          @click="onChange(resolveScaleImageAction('height').action, 'height')"
        >
          <img
            style="transform: rotate(90deg);"
            src="/img/icons/expand.svg"
          >
        </button>
        <button
          v-if="options.isSelectImageFromBackground"
          v-b-tooltip.hover
          :title="resolveScaleImageAction('default').tooltip"
          @click="onChange(resolveScaleImageAction('default').action, resolveScaleImageAction().scaleParam)"
        >
          <img
            src="/img/icons/full-screen.svg"
          >
        </button>
        <button
          v-b-tooltip.hover
          :title="resolveCenterImageAction.tooltip"
          @click="onChange(resolveCenterImageAction.action, resolveCenterImageAction)"
        >
          <img
            src="/img/icons/center-image.svg"
            alt="center-icon"
          >
        </button>
        <button
          v-b-tooltip.hover
          :title="resolveHorizontalCenterImageAction.tooltip"
          @click="onChange(resolveHorizontalCenterImageAction.action, resolveCenterImageAction)"
        >
          <img
            src="/img/icons/align-center.png"
            alt="align-center-icon"
          >
        </button>
        <button
          v-b-tooltip.hover
          class="flip-x"
          title="Flip horizontal"
          @click="onChange('flipX')"
        >
          <img src="/img/icons/horizontal-flip.svg">
        </button>
        <button
          v-b-tooltip.hover
          class="flip-y"
          title="Flip vertical"
          @click="onChange('flipY')"
        >
          <img src="/img/icons/vertical-flip.svg">
        </button>
        <button
          v-b-tooltip.hover
          class="crop"
          title="Crop"
          @click="isCropMode = true"
        >
          <svg
            class="x-svg"
            width="22"
            height="22"
          >
            <use href="/img/icons/sprite.svg#crop" />
          </svg>
        </button>
        <button
          v-if="target.originalPathForRestoreAfterCrop"
          v-b-tooltip.hover
          class="restore-original undo-button"
          title="Restore original image (crop)"
          @click="restoreOriginalAfterCrop"
        >
          <img
            src="/img/icons/reset.svg"
            alt="restore"
          >
        </button>
        <button
          v-if="isBgThemeImage"
          v-b-tooltip.hover
          class="remove-gb"
          title="Remove background"
          @click="handleRemoveBackground"
        >
          <img src="/img/icons/remove-bg.svg">
        </button>
        <button
          v-if="isFileImage"
          v-b-tooltip.hover
          class="remove-gb"
          title="Remove background"
          @click="showProductImageRemoveBGModal"
        >
          <img src="/img/icons/remove-bg.svg">
        </button>
        <button
          v-b-tooltip.hover
          title="Reset aspect ratio"
          @click="onChange('resetAspectRatio')"
        >
          <img src="/img/icons/reset-aspect-ratio.svg">
        </button>
        <button
          v-b-tooltip.hover.viewport.noninteractive="'Reset to original dimensions'"
          @click="onChange('resetToOriginalDimensions')"
        >
          <img src="/img/icons/reset-to-original-dimensions.svg">
        </button>
      </div>
    </div>
    <b-button
      variant="danger"
      @click="onChange('deleteConfirmation')"
    >
      <b-icon icon="trash" />
      Delete Layer
    </b-button>
    <button
      v-b-tooltip.hover="'Mask'"
      :data-id="selectedInstance.canvas.instance_id"
      class="btn btn-primary mask-settings"
    >
      <b-icon icon="mask" />
    </button>

    <LockToggle />

    <ProductImageRemoveBGModal
      ref="productImageRemoveBGModal"
      @replaceExistingImage="replaceImage"
      @copyAndReplaceImage="copyImage"
    />
  </div>
</template>

<script>
import { editorControlEventBroker, EVENTS } from '../../../../group/modules/editor-control-popup/event-broker';
import ProductImageDropdown from './components/ProductImageDropdown.vue';
import { LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE } from '../../../../group/modules/editor-control-popup/selected-template-for-apply-to-all';
import CropImage from './components/CropImage.vue';
import ColorPicker from './components/ColorPicker.vue';
import PositionAndSize from './components/PositionAndSize.vue'
import { mapGetters } from 'vuex'
import LockToggle from '@frontend/components/modules/layouts-page/EditorControl/components/LockToggle.vue'
import ApplyToAllImageDropdown from '@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllImageDropdown.vue';
import { DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES } from '@frontend/group/modules/additional-fields'
import FileUploadService from '@frontend/services/api/file-upload';
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID, 
  ADDITIONAL_HTML_TABLE_PREFIX_ID,
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  IMAGE_PREFIX_ID, MASK_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from "@frontend/constants/global-update";
import ObjectFillFit from "@frontend/components/modules/layouts-page/EditorControl/components/ObjectFillFit.vue";
import { parseJsonData } from '@frontend/services/helpers';
import { initDataBuilderTable } from '@frontend/group/modules/simple-table-builder/helpers';
import ProductImageRemoveBGModal from '../../file-browser/modals/ProductImageRemoveBGModal.vue';
import { RELATED_FIELD_TYPES } from "@frontend/constants/related-fields";
import Swal from 'sweetalert2';
import warningMixin from '@frontend/mixins/warningMixin';
import PositionWarning from '@frontend/components/shared/PositionWarning.vue'
import { simpleTableBuilderEventBroker, EVENTS as TABLE_EVENTS } from "@frontend/group/modules/simple-table-builder/event-broker";
import {
  EDIT_IMAGE_EVENTS,
  editImageEventBroker,
} from "@frontend/group/modules/edit-image/event-broker";
import { deleteURLParameters, setURLParameters } from "@frontend/utils";
import Multiselect from 'vue-multiselect'

const DEBOUNCE_UPDATE_TIME = 50;

const IMAGE_EDITOR_ACTIONS = {
  SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT: 'setTemplateSizeByWidthOrHeight',
  SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT: 'setBySmartObjectWidthOrHeight',
  CENTER_IMAGE: 'centerImage',
  HORIZONTAL_CENTER: 'horizontalCenter',
}

const SCALE_ACTIONS_MAP = {
  [IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT]: {
    width: 'Set to template width',
    height: 'Set to template height',
    default: 'Scale to template size'
  },
  [IMAGE_EDITOR_ACTIONS.SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT]: {
    width: 'Set to SmartObject width',
    height: 'Set to SmartObject height',
    default: 'Scale to SmartObject size'
  }
}

const BG_THEME_PREFIXES = [
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID
];

const CENTER_ACTIONS_MAP = {
  CENTER_BY_TEMPLATE_DIMENSIONS: 'Center to template size',
  CENTER_BY_SMART_OBJECT_DIMENSIONS: 'Center to SmartObject size',
  CENTER_HORIZONTALLY: 'Center Horizontally'
}

const PSD_FILE_TYPE = 'image/vnd.adobe.photoshop';

export default {
  components: {
    Multiselect,
    ObjectFillFit,
    ApplyToAllImageDropdown,
    LockToggle,
    PositionAndSize,
    ProductImageDropdown,
    CropImage,
    ColorPicker,
    ProductImageRemoveBGModal,
    PositionWarning
  },
  mixins: [warningMixin],
  props: {
    options: {
      type: Object,
      default: () => {},
    },
    target: {
      type: fabric.Object,
      default: () => {}
    },
    isTabActive: {
      type: Boolean,
    },
    themes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      LOCAL_STORAGE_KEY_SELECTED_TEMPLATES_FOR_IMAGE,
      maxStrokeWidth: 100,
      isCropMode: false,
      objectInput: {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        fileIds: '',
        isAutoPosition: false,
        isApplyToAll: false,
        fileOutlineId: '',
        fileOutlineColor: '#000000',
      },
      selectedTheme: null,
      opacity: null,
      scale: null,
      themesOptions: [],
      isCopyAndReplace: false,
      isBGCropToImage: false,
      localStrokeWidth: 0,
      isClippingMask: false
    }
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    selectedThemeObj: {
      get () {
        return _.find(this.cThemesOptions, ['id', this.selectedInstance.data.theme])
      },
      set (theme) {
        this.selectedTheme = theme?.id
      }
    },
    isScaleDisabled() {
      if (this.target.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
        const {objectFitSettings, objectFillSettings} = this.target;
        return !!objectFitSettings.align_to_fit || !!objectFillSettings.align_to_fill;
      }
      return false;
    },
    outlineData() {
      return {
        productId: this.objectInput.fileOutlineId,
        color: this.objectInput.fileOutlineColor
      }
    },
    isSmartObject() {
      return !!this.selectedInstance.canvas.getActiveObject()?.groupName;
    },
    getSmartObject() {
      return this.selectedInstance.canvas
        .getObjects()
        .find(obj => obj.groupName === this.selectedInstance.canvas.getActiveObject().groupName && obj.id.startsWith(MASK_PREFIX_ID));
    },
    resolveCenterImageAction() {
      let tooltip =  CENTER_ACTIONS_MAP.CENTER_BY_TEMPLATE_DIMENSIONS;
      let centerPoint = this.selectedInstance.canvas.getCenter();

      if (this.isSmartObject) {
        tooltip = CENTER_ACTIONS_MAP.CENTER_BY_SMART_OBJECT_DIMENSIONS;
        centerPoint = this.getSmartObject.getCenterPoint();
      }

      return {
        action: IMAGE_EDITOR_ACTIONS.CENTER_IMAGE,
        tooltip,
        centerPoint,
        isSmartObject: this.isSmartObject
      }
    },
    resolveHorizontalCenterImageAction() {
      let tooltip =  CENTER_ACTIONS_MAP.CENTER_HORIZONTALLY;
      let centerPoint = this.selectedInstance.canvas.getCenter();

      if (this.isSmartObject) {
        centerPoint = this.getSmartObject.getCenterPoint();
      }

      return {
        action: IMAGE_EDITOR_ACTIONS.HORIZONTAL_CENTER,
        tooltip,
        centerPoint,
        isSmartObject: this.isSmartObject
      }
    },
    cThemesOptions() {
      return [...this.themesOptions];
    },
    imageSrc () {
      let url = deleteURLParameters(this.target?.getSrc(), ['lightweight', 'full_resolution']);
      url = setURLParameters(url, { full_resolution: true });
      
      return url;
    },
    imageIsDefault () {
      return this.imageSrc?.includes(DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES['additional_background_images'])
    },
    isBgThemeImage() {
      return BG_THEME_PREFIXES.some(prefix => this.target.id.startsWith(prefix));
    },
    isFileImage() {
      return this.target.id.startsWith(IMAGE_PREFIX_ID);
    },
    relatedTemplateFields() {
      const fields = this.selectedInstance.template.fields
        .filter(field =>  RELATED_FIELD_TYPES.includes(field.type) && field.name !== this.target.name)
        .map(field => ({ value: field.name, text: field.name }));
      
      return [
        { value: '', text: 'Select related field' },
        ...fields
      ];
    },
    percentOfTemplate() {
      return (
        (this.target.getScaledWidth() * this.target.getScaledHeight()) / (this.target.canvas.width * this.target.canvas.height) * 100
      ).toFixed(1);
    }
  },
  watch: {
    ...(
      [
        'target.left',
        'target.top',
        'target.width',
        'target.height',
        'target.fileName',
        'target.fileOutlineId',
        'target.color',
        'target.isClippingMask'
      ].reduce(
        (acc, key) => {
          acc[key] = _.debounce(function () {
            this.initTargetData();
          }, DEBOUNCE_UPDATE_TIME)

          return acc;
        },
        {}
      )
    ),
    'target.strokeWidth': {
      immediate: true,
      handler(newVal) {
        this.localStrokeWidth = newVal;
      }
    }
  },
  mounted() {
    this.initTargetData();
    this.getThemesOptions();
  },
  methods: {
    async getThemesOptions() {
      const {data} = await axios.get(
        window.location.origin 
        + `/banner/get-template-themes?instance_id=${this.target.canvas.instance_id}` 
        + `&layout_id=${$("#template-group-preview").attr("data-layout-id")}`
      );
      this.themesOptions = data;
    },
    restoreOriginalAfterCrop() {
      console.log('restoreOriginalAfterCrop')
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action: 'undo',
        value: {
          target: this.target,
        },
      })
    },
    initTargetData() {
      const { x, y } = this.target.getPointByOrigin('left', 'top');

      this.objectInput.left = x.toFixed(2);
      this.objectInput.top = y.toFixed(2);
      this.objectInput.width = this.target.getScaledWidth().toFixed(2);
      this.objectInput.height = this.target.getScaledHeight().toFixed(2);

      if (this.target.group) {
        const matring = [...this.target.calcTransformMatrix()]

        this.objectInput.top = (matring.pop() - this.objectInput.height / 2).toFixed(2)
        this.objectInput.left = (matring.pop() - this.objectInput.width / 2).toFixed(2)
      }

      this.objectInput.isAutoPosition = !!this.target.isAutoPosition;
      this.objectInput.fileIds = this.target.fileName;
      this.objectInput.isApplyToAll = this.target.isApplyToAll;

      this.selectedTheme = this.selectedInstance.data.theme;

      // Get current image path and extract theme
      const currentSrc = this.target.getSrc();
      if (currentSrc) {
        const urlParams = new URLSearchParams(currentSrc.split('?')[1]);
        const filePath = urlParams.get('file');
        
        if (filePath && filePath.includes('files/background')) {
          const pathParts = filePath.split('/');
          const themeId = pathParts[4]; // Get themeId from URL structure
          
          if (themeId && Number.isInteger(parseInt(themeId))) {
            this.selectedTheme = themeId;
          }
        }
      }

      this.objectInput.fileOutlineId = this.target.fileOutlineId;
      this.objectInput.fileOutlineColor = this.target.color;
      this.opacity = this.target.opacity ?? 1
      this.scale = parseFloat(this.target.scaleX ?? 1).toFixed(3)
      this.isClippingMask = this.target.isClippingMask;
    },
    onChange(action, value) {
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    resolveScaleImageAction(scaleParam = null) {
      const action = this.isSmartObject
        ? IMAGE_EDITOR_ACTIONS.SET_BY_SMART_OBJECT_WIDTH_OF_HEIGHT
        : IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT;

      if (!scaleParam) {
        const canvas = action === IMAGE_EDITOR_ACTIONS.SET_TEMPLATE_SIZE_BY_WIDTH_OR_HEIGHT
          ? this.selectedInstance.canvas
          : this.getSmartObject;

        const aspectRatio = canvas.width / canvas.height;

        scaleParam = aspectRatio < 1 ? 'height' : 'width';
      }

      return {
        action,
        tooltip: SCALE_ACTIONS_MAP[action][scaleParam],
        scaleParam
      }
    },
    async handleRemoveBackground(isCropToImage = false) {
      if (this.isBgThemeImage) {
        const confirm = await Swal.fire({
          title: "Would you like to crop the image?",
          icon: "question",
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Yes, crop it!",
          denyButtonText: "No, don't crop it!",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#209c86",
          denyButtonColor: "#209c86",
        });

        if (confirm.isConfirmed) {
          this.isBGCropToImage = true;
        } else if (confirm.isDenied) {
          this.isBGCropToImage = false;
        } else {
          return;
        }
      }

      try {
        toastr.info(
          `<div class="d-inline-flex align-content-center">Removing background...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
          null,
          {
            timeOut: 0,
            tapToDismiss: false,
            closeButton: false
          })

        const filePath = new URL(this.target.getSrc()).searchParams.get('file');

        const { data } = await FileUploadService.removeImageBackground({
          filePath: filePath,
          is_crop_to_image: this.isBGCropToImage
        });

        if (this.isFileImage) {
          this.onChange('removeBackgroundProductImage', {
            imgUrl: data.imgUrl,
            filePath: filePath,
            target: this.target,
            isCopyAndReplace: this.isCopyAndReplace,
            instanceId: this.selectedInstance.id,
            isCropToImage
          });
        } else {
          this.onChange('removeBackgroundThemeImgBG', {
            imgUrl: `/share?file=${data.imgUrl}`,
            objectId: this.target.id,
            instanceId: this.selectedInstance.id,
            target: this.target
          });
        }

        toastr.clear();
      } catch (error) {
        console.log(error);
        toastr.clear();
        toastr.error('Something went wrong while removing background');
      }
    },
    isPSDFileType(file) {
      return file.type === PSD_FILE_TYPE
        || file.name.split('.').pop().toLowerCase() === 'psb'
        || file.name.split('.').pop().toLowerCase() === 'psd';
    },
    async onDrop(event) {
      if (this.options.isSelectImageFromBackground) {
        this.onImageChange('selectBackground');
        const droppedFiles = event.dataTransfer.files;
        const $newBackgroundImage = $('#new-background-image');
        const $selectBkImgModal = $('#selectBkImgModal');
        const $buttonSelect = $("#selectBkImgModal #submit");
        const $compressCheckbox = $("#compress_check")
        const $fileCaption = $newBackgroundImage.closest(".input-group").find(".file-caption-name")

        $newBackgroundImage.fileinput('readFiles', droppedFiles);
        $newBackgroundImage[0].files = droppedFiles;

        $fileCaption.val(`${droppedFiles.length} file selected`);
        $fileCaption.addClass("is-valid");

        if (droppedFiles.length) {
          droppedFiles?.length && this.isPSDFileType(droppedFiles[0])
            ? $compressCheckbox.prop('checked', false)
            : $compressCheckbox.prop('checked', true);

          $buttonSelect.hasClass('active')
            ? $buttonSelect.removeClass('active')
            : $buttonSelect.addClass('active');


          $selectBkImgModal.find('#upload').show();
          $selectBkImgModal.find('.compress_check, .crop_check').show();

        } else {
          $selectBkImgModal.find('#upload').hide();
          $selectBkImgModal.find('.compress_check, .crop_check').hide();
        }
      }

      localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);

      this.$root.$emit('productImageReplace', event)

      $('.b-sidebar').css({ border: 'none' });
    },
    onDragEnter() {
      $('.b-sidebar').css({ border: '1px solid blue' });
    },
    onDragLeave(e, forcibly = false) {
      $('.b-sidebar').css({ border: "none" });
    },
    onDragOver() {
      $('.b-sidebar').css({ border: "1px solid blue" });
    },
    onImageChange(action, value) {
      editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
        action, value
      })
    },
    initTableEditor() {
      const data = this.target.html_table_data.find((data) => {
        const table = parseJsonData(data, null);
        return table.id === this.target.field_id;
      });
      
      const table = initDataBuilderTable(parseJsonData(data, null));
      
      simpleTableBuilderEventBroker.fire(TABLE_EVENTS.INIT, {
        table,
        params: this.options.params,
        tableData: this.options.tableData,
      });
     
    },
    replaceImage(isCropToImage) {
      this.isCopyAndReplace = false;

      this.handleRemoveBackground(isCropToImage);
    },
    copyImage(isCropToImage) {
      this.isCopyAndReplace = true;

      this.handleRemoveBackground(isCropToImage);
    },
    showProductImageRemoveBGModal() {
      this.$refs.productImageRemoveBGModal.show();
    },
    showImageEditorModal() {
      const filePath = new URL(this.imageSrc).searchParams.get('file');
      if (filePath) {
        const splitFilePath = filePath.split('/');
        const fileName = splitFilePath[splitFilePath.length -1];
        this.onChange('selectBackground', {
          templateId: this.selectedInstance.template.id,
          path: filePath,
          name: fileName,
          target: this.target
        })
      }
      if (!this.selectedTheme) {
        toastr.warning('Theme is not selected');
        toastr.clear();
      }
    },
    toggleProductDropdown() {
      const dropdown = this.$refs.productImageDropdown
      if (!dropdown) return
      dropdown.onFocusProductIds(!dropdown.isShowDropdown)
    },
    handleAutoCrop({ imgUrl, filePath, isCopyAndReplace, isCropToImage, fileName }) {
      if (this.isFileImage) {
        this.onChange('removeBackgroundProductImage', {
          imgUrl,
          filePath,
          target: this.target,
          isCopyAndReplace,
          instanceId: this.selectedInstance.id,
          isCropToImage,
          isFromCropping: true
        });
      } else {
        this.onChange('selectBackground', {
          changeUploadText: true
        });

        toastr.info(
          `<div class="d-inline-flex align-content-center">Processing image...<div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div></div>`,
          null,
          {
            timeOut: 10000,
            tapToDismiss: false,
            closeButton: false
          }
        );

        const properImgUrl = imgUrl.startsWith('http') ? imgUrl : `/share?file=${imgUrl}`;
        
        fetch(properImgUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error('Network response was not ok');
            }
            return response.blob();
          })
          .then(blob => {
            const now = new Date();
            const timestamp = now.toISOString().replace(/[:.]/g, "-");
            const filename = fileName ? fileName : `image-${timestamp}.png`;
            const file = new File([blob], filename, { type: 'image/png' });

            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);

            const $newBackgroundImage = $("#new-background-image");
            const $selectBkImgModal = $("#selectBkImgModal");
            const $buttonSelect = $("#selectBkImgModal #submit");
            const $compressCheckbox = $("#compress_check");
            const $fileCaption = $newBackgroundImage
              .closest(".input-group")
              .find(".file-caption-name");

            $newBackgroundImage.fileinput("readFiles", dataTransfer.files);
            $newBackgroundImage[0].files = dataTransfer.files;

            $fileCaption.val(`${dataTransfer.files.length} file selected`);
            $fileCaption.addClass("is-valid");

            if (dataTransfer.files.length) {
              $compressCheckbox.prop("checked", true);

              $buttonSelect.hasClass("active")
                ? $buttonSelect.removeClass("active")
                : $buttonSelect.addClass("active");

              $selectBkImgModal.find("#upload").show();
              $selectBkImgModal.find(".compress_check, .crop_check").show();
            } else {
              $selectBkImgModal.find("#upload").hide();
              $selectBkImgModal.find(".compress_check, .crop_check").hide();
            }

            localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);

            this.$root.$emit("productImageReplace", {
              dataTransfer,
            });
            
            this.onChange('removeBackgroundThemeImgBG', {
              imgUrl: `/share?file=${imgUrl}`,
              objectId: this.target.id,
              instanceId: this.selectedInstance.id,
              target: this.target,
              isFromCropping: true
            });
            
            toastr.clear();
          })
          .catch(error => {
            console.error('Error fetching image:', error);
            toastr.clear();
            toastr.error('Failed to process the image. Please try again.');
            
            this.onChange('removeBackgroundThemeImgBG', {
              imgUrl: `/share?file=${imgUrl}`,
              objectId: this.target.id,
              instanceId: this.selectedInstance.id,
              target: this.target,
              isFromCropping: true
            });
          });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.img-preview {
  max-height: 38px;
}

.download-image {
  position: relative; 
  display: inline-block;
  text-decoration: none;
  text-align: center; 
  padding: 0 22px;
  border: 1px solid #1c7964;
  background: white;
  box-sizing: border-box;
  border-radius: 10px;
}

.download-image:hover {
  text-decoration: none;
}

.download-image-href {
  position: absolute;
  top: 0;
  left: 3px;
  opacity: 0; /* Hide initially */
  transition: opacity 0.3s ease-in-out;
}

.edit-image-btn {
  position: absolute;
  top: 16px;
  left: 3px;
  opacity: 0; /* Hide initially */
  transition: opacity 0.3s ease-in-out;
  border: 0;
  background: transparent;
  padding: 0;
  color: #209c86;
}

.download-image:hover .download-image-href,
.download-image:hover .edit-image-btn {
  opacity: 1; /* Show both elements when hovering */
}


.close-btn {
  opacity: 0; /* Hide initially */
  position: absolute;
  top: 4px;
  right: 3px;
  background-color: red;
  vertical-align: -.15em;
  color: white;
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  transition: opacity 0.3s ease-in-out;
}

.download-image:hover .close-btn{
  opacity: 1; /* Hide initially */
}

.position-warning {
  font-size: 0.9rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  
  .warning-actions {
    .btn-sm {
      display: flex !important;
      align-items: center !important;
      min-width: auto;
      padding: 0.25rem 0.75rem;
      color: #000 !important;
    }
  }
}
</style>
